import { emptyDocument, newEntityId, type CadDocument, type Layer } from '../../model/document';
import type { BlockDef, CadEntity } from '../../model/entity';
import { pt, type Pt } from '../../model/geom';
import { degToRad, mm, normalizeAngle, type Micron } from '../../model/units';
import type { ConversionWarning } from '../adapter';
import { aciToHex, ACI_BY_LAYER } from './aci';
import { int, isBinaryDxf, num, tokenize, type DxfPair } from './groupCodes';

/**
 * DXF 리더 (작업지시서 P3-02)
 *
 * 지원: LINE · LWPOLYLINE · POLYLINE/VERTEX · CIRCLE · ARC · TEXT · MTEXT · INSERT
 * 폴리선으로 근사: ELLIPSE(타원) · SPLINE(곡선) · POINT(점, 작은 원으로)
 *   — 우리 도형 모델에 곡선(베지어·NURBS) 자체가 없어서 다각형으로 잘게
 *     쪼개 넣는다. 매끄러운 곡선으로 다시 내보낼 수는 없지만, 도면 검토·
 *     물량 산출에는 이 정도 근사로 충분하고 "형체가 통째로 사라지는 것"보다
 *     훨씬 낫다.
 * 읽되 경고: HATCH · DIMENSION · SOLID · 3DFACE
 *   — HATCH 는 대개 이미 그려진 경계선 위의 채우기 패턴이라, 경계 자체는
 *     보통 다른 선/폴리선으로 도면에 남아 있다. 채우기 무늬만 빠진다.
 *
 * 설계 원칙:
 *   1. **절대 던지지 않는다.** 실무 도면에는 잡음이 섞여 있고, 파일 하나가
 *      통째로 실패하면 사용자는 원인을 알 수 없다. 못 읽은 것은 경고로 남기고
 *      읽을 수 있는 것만이라도 살려낸다.
 *   2. 좌표는 도면 단위 → 정수 µm 로 변환한다. 변환 계수는 $INSUNITS 로 정한다.
 *   3. 원본을 바꾸지 않는다. 여기서 만든 것은 항상 새 문서다.
 */

/** DXF 개체 하나 = 타입 + 그룹코드 묶음 */
interface Record0 {
  type: string;
  pairs: DxfPair[];
}

export function readDxf(buf: ArrayBuffer): { doc: CadDocument; warnings: ConversionWarning[] } {
  const warnings: ConversionWarning[] = [];

  if (isBinaryDxf(buf)) {
    // 바이너리 DXF 는 변환 워커(LibreDWG)로 넘긴다.
    // 여기서 어설프게 반쪽 구현하는 것보다 경계를 분명히 하는 편이 안전하다.
    warnings.push({
      code: 'binary_dxf',
      message:
        '바이너리 DXF 는 브라우저에서 직접 읽지 않습니다. 변환 서버를 거쳐 처리됩니다.',
    });
    return { doc: emptyDocument('mm'), warnings };
  }

  const text = decodeText(buf);
  const { pairs, skipped } = tokenize(text);
  if (skipped > 0) {
    warnings.push({
      code: 'parse_recovered',
      message: `형식이 어긋난 줄 ${skipped}개를 건너뛰고 복구했습니다.`,
      count: skipped,
    });
  }

  const doc = emptyDocument('mm');
  doc.layers = []; // DXF 의 레이어 테이블로 채운다. 비어 있으면 아래에서 표준을 넣는다.

  /* --------------------------------------------------- 섹션 분리 */

  const sections = splitSections(pairs);

  // 1) 단위 계수
  const insUnits = readInsUnits(sections.get('HEADER') ?? []);
  let scale = unitScale(insUnits);

  /*
   * 단위 자동 검증 — $INSUNITS 가 틀린 파일을 걸러낸다.
   *
   * 지금 계수로 읽었을 때 도면 크기가 건물이라고 볼 수 없으면(예: 매장이
   * 5.4km) 표기를 믿지 않고, 도면에 기입된 치수를 근거로 계수를 다시 고른다.
   * 크기가 멀쩡하면 아무것도 건드리지 않는다 — 멀쩡한 파일에 손대지 않는 것이
   * 이 검사의 첫 번째 원칙이다.
   */
  const extentUnits = readExtentSize(sections.get('HEADER') ?? []);
  const labelEvidence = detectUnitFactor(
    sections.get('BLOCKS') ?? [],
    sections.get('ENTITIES') ?? []
  );
  let unitFixed: { from: number; to: number } | null = null;

  if (extentUnits !== null && clearlyTooBig(extentUnits * scale)) {
    // 치수 글자가 mm 라는 전제에서 나온 계수를 1순위로, 흔한 계수들을 그다음으로 본다
    const candidates = [
      ...(labelEvidence ? [1_000 / labelEvidence.factor] : []),
      1_000, // mm
      10_000, // cm
      1_000_000, // m
      25_400, // inch
      304_800, // feet
    ];
    const better = candidates.find(
      (s) => s > 0 && Number.isFinite(s) && sensibleDrawingSize(extentUnits * s)
    );
    if (better !== undefined && Math.abs(better - scale) / scale > 0.01) {
      unitFixed = { from: scale, to: better };
      scale = better;
    }
  }

  if (unitFixed) {
    warnings.push({
      code: 'precision_rounded',
      subject: '단위 자동 보정',
      message:
        `도면에 적힌 단위($INSUNITS=${insUnits})대로 읽으면 도면이 ` +
        `${Math.round((extentUnits! * unitFixed.from) / 1_000_000)}m 크기가 되어 앞뒤가 맞지 않아, ` +
        `${Math.round((extentUnits! * unitFixed.to) / 1_000_000)}m 로 바로잡았습니다` +
        (labelEvidence ? ` (도면에 기입된 치수 ${labelEvidence.samples}개를 근거로 판단).` : '.') +
        ' 치수가 여전히 안 맞으면 축척 보정(CAL) 명령을 쓰세요.',
    });
  } else if (insUnits === 0) {
    warnings.push({
      code: 'precision_rounded',
      message: '도면에 단위 정보($INSUNITS)가 없어 mm 로 간주했습니다.',
    });
  }

  // 2) 레이어 테이블
  for (const l of readLayers(sections.get('TABLES') ?? [])) doc.layers.push(l);

  // 도면이 참조하는데 테이블에 없는 레이어는 즉석에서 만든다.
  // 이걸 안 하면 그 엔티티들이 통째로 보이지 않게 된다.
  const known = new Set(doc.layers.map((l) => l.code));
  const ensureLayer = (layerName: string): void => {
    if (known.has(layerName)) return;
    known.add(layerName);
    doc.layers.push(defaultLayer(layerName));
    warnings.push({
      code: 'layer_created',
      message: `레이어 테이블에 없는 '${layerName}' 을(를) 자동 생성했습니다.`,
      subject: layerName,
    });
  };

  // 3) 블록 정의 (+ *Model_Space 안에 들어 있던 모델 공간 도형, 치수 익명 블록)
  const { blocks, modelSpace, anonymousBlocks } = readBlocks(
    sections.get('BLOCKS') ?? [],
    scale,
    warnings,
    ensureLayer
  );
  doc.blocks = blocks;

  // 4) 엔티티
  doc.entities = readEntities(
    sections.get('ENTITIES') ?? [],
    scale,
    warnings,
    ensureLayer,
    anonymousBlocks
  );

  /*
   * ★ 모델 공간이 비었는데 `*Model_Space` 블록에 도형이 들어 있으면 그걸 쓴다.
   *
   *   DWG 는 모델 공간 도형을 `*Model_Space` 블록 레코드가 소유한다. 그래서
   *   DWG→DXF 변환기가 ENTITIES 섹션을 비워 두고 그 블록 안에 전부 넣어
   *   내보내는 경우가 있다. 실제로 AutoCAD 2013(AC1027) 도면에서 그랬고,
   *   화면에 아무것도 안 나온다는 신고로 이어졌다.
   *
   *   ENTITIES 가 이미 있으면 손대지 않는다 — 둘 다 있으면 도형이 두 번 그려진다.
   */
  if (doc.entities.length === 0 && modelSpace.length > 0) {
    doc.entities = modelSpace;
    warnings.push({
      code: 'parse_recovered',
      message: `모델 공간이 비어 있어 '*Model_Space' 블록에서 도형 ${modelSpace.length}개를 되살렸습니다.`,
      count: modelSpace.length,
    });
  }

  if (doc.layers.length === 0) doc.layers.push(defaultLayer('0'));

  /*
   * 화면이 텅 비는 경우를 **말없이 넘기지 않는다.**
   *
   * 읽을 수 있는 도형이 하나도 없는데 생략된 객체만 잔뜩이면, 사용자는 빈 캔버스를
   * 보고 "먹통" 이라고 판단한다. 실제로 그런 신고가 있었다(전부 SOLID 인 도면).
   * 무엇 때문에 비었는지, 무엇을 하면 되는지를 그 자리에서 알려준다.
   */
  if (doc.entities.length === 0) {
    const dropped = warnings
      .filter((w) => w.code === 'unsupported_entity')
      .reduce((sum, w) => sum + (w.count ?? 1), 0);

    // 무엇이 들어 있었는지 숫자로 남긴다 — 원격에서 원인을 좁히는 유일한 단서다.
    // ★ 도형이 가장 많은 블록 이름까지 보여준다. 도형이 한 블록에 몰려 있으면
    //   그게 사실상 모델 공간인데 이름이 달라 못 알아본 것이고, 여러 블록에
    //   흩어져 있으면 그것들을 배치할 INSERT 를 변환기가 잃어버린 것이다.
    const inBlocks = doc.blocks.reduce((n, b) => n + b.entities.length, 0);
    const top = [...doc.blocks]
      .sort((a, b) => b.entities.length - a.entities.length)
      .slice(0, 3)
      .filter((b) => b.entities.length > 0)
      .map((b) => `${b.code}:${b.entities.length}`)
      .join(', ');
    const census =
      `블록 ${doc.blocks.length}개(도형 ${inBlocks}개) · 생략된 객체 ${dropped}개` +
      (top ? ` · 큰 블록 ${top}` : '');

    warnings.push({
      code: 'unsupported_entity',
      message:
        dropped > 0
          ? `읽을 수 있는 도형이 없어 화면이 비어 있습니다. 위에 생략된 객체들이 도면의 전부였습니다. 원본을 AutoCAD 에서 DXF(R2000 이상)로 저장해 다시 올려 보세요. [${census}]`
          : `읽을 수 있는 도형이 없습니다. 빈 도면이거나, 도형이 외부 참조(Xref)로만 이루어져 있을 수 있습니다. [${census}]`,
      subject: 'EMPTY_RESULT',
    });
  }

  return { doc, warnings };
}

/* -------------------------------------------------------------------------- *
 * 인코딩
 * -------------------------------------------------------------------------- */

/**
 * 한국 현장 도면은 CP949(EUC-KR)로 저장된 것이 많다.
 * UTF-8 로 먼저 시도하고, 치환문자(U+FFFD)가 나오면 CP949 로 다시 읽는다.
 */
function decodeText(buf: ArrayBuffer): string {
  const utf8 = new TextDecoder('utf-8').decode(buf);
  if (!utf8.includes('�')) return utf8;
  try {
    return new TextDecoder('euc-kr').decode(buf);
  } catch {
    return utf8;
  }
}

/* -------------------------------------------------------------------------- *
 * 섹션 · 헤더
 * -------------------------------------------------------------------------- */

function splitSections(pairs: DxfPair[]): Map<string, DxfPair[]> {
  const out = new Map<string, DxfPair[]>();
  let current: string | null = null;
  let buf: DxfPair[] = [];

  for (let i = 0; i < pairs.length; i++) {
    const p = pairs[i]!;
    if (p.code === 0 && p.value === 'SECTION') {
      const namePair = pairs[i + 1];
      current = namePair && namePair.code === 2 ? namePair.value.trim() : null;
      buf = [];
      i++; // 이름 쌍 소비
      continue;
    }
    if (p.code === 0 && p.value === 'ENDSEC') {
      if (current) out.set(current, buf);
      current = null;
      buf = [];
      continue;
    }
    if (current) buf.push(p);
  }
  if (current && buf.length > 0) out.set(current, buf);
  return out;
}

/** $INSUNITS — 0=없음 1=inch 4=mm 5=cm 6=m */
function readInsUnits(header: DxfPair[]): number {
  for (let i = 0; i < header.length; i++) {
    if (header[i]!.code === 9 && header[i]!.value.trim() === '$INSUNITS') {
      const v = header[i + 1];
      if (v && v.code === 70) return int(v.value, 0);
    }
  }
  return 0;
}

/** 도면 단위 1 → µm 몇 개인가 */
function unitScale(insUnits: number): number {
  switch (insUnits) {
    case 1:
      return 25_400; // inch
    case 2:
      return 304_800; // feet
    case 5:
      return 10_000; // cm
    case 6:
      return 1_000_000; // m
    case 4:
    default:
      return 1_000; // mm (기본)
  }
}

/* -------------------------------------------------------------------------- *
 * 단위 자동 검증 — 도면이 스스로 적어 둔 치수를 근거로 삼는다
 * -------------------------------------------------------------------------- */

/**
 * ★ 왜 필요한가 — `$INSUNITS` 를 믿을 수 없기 때문이다.
 *
 *   실무 DWG 에는 이 값이 아예 없거나(0), mm 로 그려 놓고 inch(1)로 적혀
 *   있는 파일이 흔하다. 변환기를 거치면 더 흔해진다. 그러면 좌표가 통째로
 *   **정확히 25.4배**(inch→mm) 로 부풀어, 1,390mm 벽 간격이 35,306mm 로
 *   읽힌다. 실제로 겪은 신고가 그 숫자였다.
 *
 *   도면 자신이 정답을 갖고 있다: 설계자가 기입한 치수(DIMENSION)의 **글자
 *   값**이다. 좌표 거리와 그 글자 값의 비율이 전 도면에 걸쳐 일정하다면,
 *   "파일 좌표 1 = 글자 단위 몇" 을 알 수 있다. 국내 건축·리테일 도면은
 *   치수를 mm 로 적으므로, 거기서 실제 계수가 나온다.
 *
 *   다만 이것만으로 덮어쓰면 위험하다(치수를 m 로 적은 도면도 있다).
 *   그래서 **지금 계수로 읽었을 때 도면 크기가 말이 안 될 때만** 고친다.
 *   매장 도면이 5.4km 로 읽히면 그건 확실히 틀린 것이다.
 */

/** 치수 글자에서 숫자를 뽑는다. MTEXT 서식 코드(`\A1;` `\H40x;`)를 먼저 걷어낸다. */
function dimTextValue(raw: string): number | null {
  const cleaned = raw
    .replace(/\\[A-Za-z][^;\\]*;/g, '') // \A1;  \H40x;  \f...;
    .replace(/\\[A-Za-z]/g, ' ') // \P \X 등 단독 코드
    .replace(/[{}]/g, '')
    .replace(/[,\s]/g, '');
  const m = cleaned.match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const v = Number(m[1]);
  return Number.isFinite(v) && v > 0 ? v : null;
}

function detectUnitFactor(
  blocksSection: DxfPair[],
  entitiesSection: DxfPair[]
): { factor: number; samples: number } | null {
  // 치수 하나마다 오토캐드가 만들어 둔 익명 블록(*D…) 안에 표기 글자가 들어 있다
  const labelOf = new Map<string, number>();
  let current: string | null = null;
  for (const rec of splitByCode0(blocksSection)) {
    if (rec.type === 'BLOCK') {
      current = firstString(rec, 2) ?? null;
      continue;
    }
    if (rec.type === 'ENDBLK') {
      current = null;
      continue;
    }
    if (!current || !current.startsWith('*') || labelOf.has(current)) continue;
    if (rec.type !== 'TEXT' && rec.type !== 'MTEXT') continue;
    const raw = rec.pairs.filter((p) => p.code === 1).map((p) => p.value).join('');
    const v = dimTextValue(raw);
    if (v !== null) labelOf.set(current, v);
  }
  if (labelOf.size === 0) return null;

  const ratios: number[] = [];
  for (const rec of splitByCode0(entitiesSection)) {
    if (rec.type !== 'DIMENSION') continue;
    const name = firstString(rec, 2);
    const label = name ? labelOf.get(name) : undefined;
    if (label === undefined) continue;

    // 13/23 · 14/24 = 선형 치수의 두 측정점 (파일 단위 그대로)
    const x1 = allNums(rec, 13)[0];
    const y1 = allNums(rec, 23)[0];
    const x2 = allNums(rec, 14)[0];
    const y2 = allNums(rec, 24)[0];
    if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) continue;

    const geom = Math.hypot(x2 - x1, y2 - y1);
    if (geom <= 0) continue;
    ratios.push(geom / label);
  }
  if (ratios.length < 8) return null;

  ratios.sort((a, b) => a - b);
  const median = ratios[Math.floor(ratios.length / 2)]!;
  if (!Number.isFinite(median) || median <= 0) return null;

  const agree = ratios.filter((r) => Math.abs(r - median) / median < 0.01).length;
  if (agree / ratios.length < 0.6) return null; // 제각각이면 판단하지 않는다

  return { factor: median, samples: ratios.length };
}

/** $EXTMIN/$EXTMAX 로 도면이 파일 단위로 얼마나 넓은지 (없으면 null) */
function readExtentSize(header: DxfPair[]): number | null {
  const read = (name: string): { x: number; y: number } | null => {
    for (let i = 0; i < header.length; i++) {
      if (header[i]!.code !== 9 || header[i]!.value.trim() !== name) continue;
      let x: number | null = null;
      let y: number | null = null;
      for (let j = i + 1; j < header.length && header[j]!.code !== 9; j++) {
        if (header[j]!.code === 10) x = Number(header[j]!.value);
        if (header[j]!.code === 20) y = Number(header[j]!.value);
      }
      if (x !== null && y !== null && Number.isFinite(x) && Number.isFinite(y)) return { x, y };
    }
    return null;
  };
  const lo = read('$EXTMIN');
  const hi = read('$EXTMAX');
  if (!lo || !hi) return null;
  const size = Math.max(hi.x - lo.x, hi.y - lo.y);
  return Number.isFinite(size) && size > 0 ? size : null;
}

/**
 * 확실히 틀렸다고 볼 수 있는 크기 (µm). 2km 넘는 건축 도면은 없다.
 *
 * ★ 일부러 "너무 크다" 쪽만 본다. 작은 쪽은 못 건드린다 — 1m 짜리 상세도나
 *   부분 도면은 실제로 존재하기 때문이다(골든 픽스처가 딱 그랬고, 여기에
 *   하한을 걸었더니 멀쩡한 도면을 10배로 키우는 오탐이 났다).
 */
function clearlyTooBig(um: number): boolean {
  return um > 2_000_000_000;
}

/** 보정 후보로 받아들일 수 있는 크기 (µm) — 0.5m ~ 2km */
function sensibleDrawingSize(um: number): boolean {
  return um >= 500_000 && um <= 2_000_000_000;
}

/* -------------------------------------------------------------------------- *
 * 레이어 테이블
 * -------------------------------------------------------------------------- */

function readLayers(tables: DxfPair[]): Layer[] {
  const out: Layer[] = [];
  const records = splitByCode0(tables);
  let order = 0;

  for (const rec of records) {
    if (rec.type !== 'LAYER') continue;

    const name = firstString(rec, 2) ?? '';
    if (name === '') continue;

    const aci = firstInt(rec, 62) ?? 7;
    const flags = firstInt(rec, 70) ?? 0;
    const lineType = firstString(rec, 6) ?? 'continuous';
    const lw = firstInt(rec, 370) ?? 25;

    out.push({
      code: name,
      name,
      // ACI 가 음수면 "레이어 꺼짐"을 뜻한다 (AutoCAD 관례)
      color: aciToHex(Math.abs(aci)) ?? '#CCCCCC',
      lineType: lineType.toLowerCase() === 'continuous' ? 'continuous' : lineType,
      lineWeight: lw > 0 ? lw : 25,
      visible: aci >= 0 && (flags & 1) === 0,
      locked: (flags & 4) !== 0,
      printable: true,
      sortOrder: order++,
    });
  }
  return out;
}

function defaultLayer(name: string): Layer {
  return {
    code: name,
    name,
    color: '#CCCCCC',
    lineType: 'continuous',
    lineWeight: 25,
    visible: true,
    locked: false,
    printable: true,
    sortOrder: 500,
  };
}

/* -------------------------------------------------------------------------- *
 * 블록
 * -------------------------------------------------------------------------- */

/**
 * 모델 공간 블록인가.
 *
 * 표기가 변환기마다 흔들린다: `*Model_Space`, `*MODEL_SPACE`, `$MODEL_SPACE`,
 * 드물게 접두어 없이 `MODEL_SPACE`. 접두어(*, $)와 밑줄·공백을 털어내고 비교한다.
 */
function isModelSpaceBlock(name: string): boolean {
  return name.replace(/^[*$]/, '').replace(/[_\s]/g, '').toUpperCase() === 'MODELSPACE';
}

function readBlocks(
  section: DxfPair[],
  scale: number,
  warnings: ConversionWarning[],
  ensureLayer: (name: string) => void
): { blocks: BlockDef[]; modelSpace: CadEntity[]; anonymousBlocks: Map<string, CadEntity[]> } {
  const out: BlockDef[] = [];
  /**
   * `*Model_Space` 블록 안에 들어 있던 도형.
   *
   * ★ DWG 내부에서는 모델 공간 도형이 `*Model_Space` 블록 레코드에 **소유된다.**
   *   그래서 DWG→DXF 변환기가 이것들을 ENTITIES 섹션이 아니라 그 블록 정의
   *   안에 그대로 쏟아 놓는 경우가 있다. 예전에는 이름이 `*` 로 시작한다는
   *   이유로 통째로 버려서, 도형이 멀쩡히 들어 있는데도 화면이 비었다.
   *   여기서 따로 받아 두었다가 모델 공간이 비었을 때 되살린다.
   */
  const modelSpace: CadEntity[] = [];
  /**
   * 익명 블록(`*` 로 시작, `*Model_Space` 제외) — 치수(DIMENSION) 하나마다
   * 오토캐드가 이미 계산해서 그려 둔 치수보조선·화살표·문자를 담고 있다.
   * DIMENSION 엔티티가 group 2 로 이 이름을 참조하므로, readEntities() 에서
   * 그대로 꺼내 쓸 수 있게 이름별로 모아 둔다(치수 되살리기, 신규 요청).
   *
   * 좌표는 절대좌표 그대로 둔다 — 이 블록들의 삽입기준점은 항상 원점이라
   * *Model_Space 와 마찬가지로 이동시키면 안 된다.
   */
  const anonymousBlocks = new Map<string, CadEntity[]>();
  const records = splitByCode0(section);

  let currentName: string | null = null;
  let basePoint: Pt = pt(0, 0);
  let buffer: Record0[] = [];

  for (const rec of records) {
    if (rec.type === 'BLOCK') {
      currentName = firstString(rec, 2) ?? null;
      basePoint = readPoint(rec, 10, scale);
      buffer = [];
      continue;
    }
    if (rec.type === 'ENDBLK') {
      if (currentName) {
        const modelSpaceBlock = isModelSpaceBlock(currentName);
        const anonymous = currentName.startsWith('*') && !modelSpaceBlock;
        const entities: CadEntity[] = [];
        for (const child of buffer) {
          // 모델 공간·치수 익명 블록 도형은 도면에 그대로 나오므로 레이어도 실제로 만들어 줘야 한다
          const e = recordToEntity(
            child,
            scale,
            warnings,
            modelSpaceBlock || anonymous ? ensureLayer : () => {}
          );
          if (!e) continue;
          if (modelSpaceBlock || anonymous) {
            // 절대좌표다 — 삽입점 기준으로 옮기면 안 된다
            if (modelSpaceBlock) modelSpace.push(e);
            else entities.push(e);
          } else {
            // 블록 내부 좌표는 삽입점 기준 상대좌표로 만든다
            entities.push(shiftEntity(e, -basePoint.x, -basePoint.y));
          }
        }
        if (anonymous) {
          anonymousBlocks.set(currentName, entities);
        } else if (!modelSpaceBlock) {
          // 모델 공간 블록은 재사용하는 블록이 아니므로 라이브러리에서 뺀다
          // (안 그러면 빈 껍데기가 블록 목록과 진단 숫자에 섞인다).
          out.push({ code: currentName, name: currentName, entities });
        }
      }
      currentName = null;
      buffer = [];
      continue;
    }
    if (currentName) buffer.push(rec);
  }
  return { blocks: out, modelSpace, anonymousBlocks };
}

function shiftEntity(e: CadEntity, dx: number, dy: number): CadEntity {
  const f = (p: Pt): Pt => ({ x: p.x + dx, y: p.y + dy });
  switch (e.type) {
    case 'line':
      return { ...e, a: f(e.a), b: f(e.b) };
    case 'polyline':
      return { ...e, points: e.points.map(f) };
    case 'circle':
    case 'arc':
      return { ...e, center: f(e.center) };
    case 'text':
      return { ...e, at: f(e.at) };
    case 'blockref':
      return { ...e, at: f(e.at) };
    case 'dimension':
      return { ...e, a: f(e.a), b: f(e.b), offsetPoint: f(e.offsetPoint) };
  }
}

/* -------------------------------------------------------------------------- *
 * 엔티티
 * -------------------------------------------------------------------------- */

function readEntities(
  section: DxfPair[],
  scale: number,
  warnings: ConversionWarning[],
  ensureLayer: (name: string) => void,
  anonymousBlocks: Map<string, CadEntity[]>
): CadEntity[] {
  const out: CadEntity[] = [];
  const records = splitByCode0(section);
  let recoveredDimensions = 0;

  for (let i = 0; i < records.length; i++) {
    const rec = records[i]!;

    /*
     * 치수(DIMENSION) — 값을 직접 계산해서 만들지 않는다. 대신 오토캐드가
     * 이미 그려 둔 결과(치수보조선·화살표·문자)를 group 2 가 가리키는 익명
     * 블록에서 그대로 꺼내 쓴다. DIMENSION 의 그룹코드 의미(선형/정렬/반지름
     * 마다 기준점이 전부 다르다)를 다시 구현하는 것보다 훨씬 정확하다 —
     * 오토캐드의 치수 스타일(자릿수·접두/접미사 등)까지 그대로 살아난다.
     * 대신 우리 쪽 DimensionEntity(재계산되는 살아있는 치수)는 아니고,
     * 고정된 선·문자 몇 개일 뿐이라 나중에 끝점을 옮겨도 값이 안 바뀐다.
     */
    if (rec.type === 'DIMENSION') {
      const blockName = firstString(rec, 2);
      const source = blockName ? anonymousBlocks.get(blockName) : undefined;
      if (source && source.length > 0) {
        for (const e of source) out.push({ ...e, id: newEntityId() });
        recoveredDimensions++;
      } else {
        warnings.push({
          code: 'unsupported_entity',
          message: '치수는 아직 읽지 못해 생략했습니다.',
          subject: 'DIMENSION',
        });
      }
      continue;
    }

    // 구형 POLYLINE 은 뒤따르는 VERTEX 들을 모아야 완성된다
    if (rec.type === 'POLYLINE') {
      const verts: Pt[] = [];
      let j = i + 1;
      while (j < records.length && records[j]!.type === 'VERTEX') {
        verts.push(readPoint(records[j]!, 10, scale));
        j++;
      }
      if (j < records.length && records[j]!.type === 'SEQEND') j++;
      i = j - 1;

      if (verts.length >= 2) {
        const layer = firstString(rec, 8) ?? '0';
        ensureLayer(layer);
        out.push({
          id: newEntityId(),
          type: 'polyline',
          layer,
          color: colorOf(rec),
          points: verts,
          closed: ((firstInt(rec, 70) ?? 0) & 1) !== 0,
        });
      }
      continue;
    }

    const e = recordToEntity(rec, scale, warnings, ensureLayer);
    if (e) out.push(e);
  }

  if (recoveredDimensions > 0) {
    warnings.push({
      code: 'parse_recovered',
      message: `치수 ${recoveredDimensions}개를 원본 그대로(선·문자) 가져왔습니다 — 끝점을 옮겨도 값은 안 바뀝니다.`,
      count: recoveredDimensions,
    });
  }
  return out;
}

// ELLIPSE · SPLINE · POINT · SOLID · 3DFACE 는 자체 case 에서 처리한다.
// 이 표에는 정말로 못 읽는 것만 남긴다.
const UNSUPPORTED_NOTE: Record<string, string> = {
  HATCH: '해치(면 채우기)',
  DIMENSION: '치수',
  LEADER: '지시선',
  ATTDEF: '블록 속성 정의',
};

/**
 * SOLID / 3DFACE 를 닫힌 폴리선으로 읽는다.
 *
 * ★ 실무에서 이게 빠지면 도면이 통째로 비어 보인다.
 *   국내 건축 DWG 는 벽체·기둥을 채워진 SOLID 로 그리는 경우가 많아서,
 *   실제로 한 도면에서 SOLID 406개가 전부 버려져 화면에 아무것도 안 나온 적이 있다.
 *
 * ⚠️ SOLID 의 꼭짓점 순서는 사각형 순회 순서가 아니라 **지그재그**다.
 *   (10,20)=1번, (11,21)=2번, (12,22)=3번, (13,23)=4번 이고
 *   실제 도형은 1→2→4→3 순서로 이어진다. 이걸 모르고 1→2→3→4 로 이으면
 *   나비넥타이 모양으로 꼬인다. 3DFACE 는 반대로 1→2→3→4 가 맞다.
 */
function quadToPolyline(
  rec: Record0,
  scale: number,
  zigzag: boolean
): { points: Pt[]; closed: boolean } | null {
  const p1 = readPoint(rec, 10, scale);
  const p2 = readPoint(rec, 11, scale);
  const p3 = readPoint(rec, 12, scale);
  // 4번째 점이 없으면 삼각형이다 — DXF 는 그때 3번을 그대로 반복한다.
  const has4 = firstNum(rec, 13) !== null;
  const p4 = has4 ? readPoint(rec, 13, scale) : p3;

  const ordered = zigzag ? [p1, p2, p4, p3] : [p1, p2, p3, p4];

  // 연속 중복점 제거 (삼각형이면 4번이 3번과 같다)
  const points: Pt[] = [];
  for (const p of ordered) {
    const last = points[points.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) points.push(p);
  }
  // 첫 점과 끝 점이 같으면 닫힘 처리에서 중복되므로 뺀다
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (points.length > 2 && first.x === last.x && first.y === last.y) points.pop();

  if (points.length < 3) return null;
  return { points, closed: true };
}

function recordToEntity(
  rec: Record0,
  scale: number,
  warnings: ConversionWarning[],
  ensureLayer: (name: string) => void
): CadEntity | null {
  const layer = firstString(rec, 8) ?? '0';
  const common = () => {
    ensureLayer(layer);
    return { id: newEntityId(), layer, color: colorOf(rec) };
  };

  switch (rec.type) {
    case 'LINE':
      return { ...common(), type: 'line', a: readPoint(rec, 10, scale), b: readPoint(rec, 11, scale) };

    case 'LWPOLYLINE': {
      const xs = allNums(rec, 10).map((v) => Math.round(v * scale));
      const ys = allNums(rec, 20).map((v) => Math.round(v * scale));
      const n = Math.min(xs.length, ys.length);
      if (n < 2) return null;
      const points: Pt[] = [];
      for (let k = 0; k < n; k++) points.push({ x: xs[k]!, y: ys[k]! });
      return {
        ...common(),
        type: 'polyline',
        points,
        closed: ((firstInt(rec, 70) ?? 0) & 1) !== 0,
      };
    }

    case 'CIRCLE': {
      const r = Math.round((firstNum(rec, 40) ?? 0) * scale);
      if (r <= 0) return null;
      return { ...common(), type: 'circle', center: readPoint(rec, 10, scale), radius: r };
    }

    case 'ARC': {
      const r = Math.round((firstNum(rec, 40) ?? 0) * scale);
      if (r <= 0) return null;
      return {
        ...common(),
        type: 'arc',
        center: readPoint(rec, 10, scale),
        radius: r,
        startAngle: normalizeAngle(degToRad(firstNum(rec, 50) ?? 0)),
        endAngle: normalizeAngle(degToRad(firstNum(rec, 51) ?? 0)),
      };
    }

    case 'TEXT':
    case 'MTEXT': {
      const raw = firstString(rec, 1) ?? '';
      if (raw.trim() === '') return null;
      const h = Math.round((firstNum(rec, 40) ?? 2.5) * scale);
      const halign = firstInt(rec, 72) ?? 0;
      return {
        ...common(),
        type: 'text',
        at: readPoint(rec, 10, scale),
        // MTEXT 는 서식 코드가 섞여 있다. 도면 검토용으로는 평문이면 충분하다.
        text: rec.type === 'MTEXT' ? stripMTextFormatting(raw) : raw,
        height: h > 0 ? h : Math.round(2.5 * scale),
        rotation: normalizeAngle(degToRad(firstNum(rec, 50) ?? 0)),
        align: halign === 1 ? 'center' : halign === 2 ? 'right' : 'left',
      };
    }

    case 'INSERT': {
      const blockCode = firstString(rec, 2) ?? '';
      if (blockCode === '') return null;
      const sx = firstNum(rec, 41) ?? 1;
      return {
        ...common(),
        type: 'blockref',
        blockCode,
        at: readPoint(rec, 10, scale),
        rotation: normalizeAngle(degToRad(firstNum(rec, 50) ?? 0)),
        scale: sx === 0 ? 1 : sx,
        attributes: {},
      };
    }

    case 'ELLIPSE': {
      const approx = ellipseToPolyline(rec, scale);
      if (!approx) {
        warnings.push({
          code: 'unsupported_entity',
          message: '타원 정보가 불완전해 생략했습니다.',
          subject: 'ELLIPSE',
        });
        return null;
      }
      warnings.push({
        code: 'curve_approximated',
        message: '타원을 다각형으로 근사했습니다 (매끄러운 곡선으로 다시 내보낼 수는 없습니다).',
        subject: 'ELLIPSE',
      });
      return { ...common(), type: 'polyline', points: approx.points, closed: approx.closed };
    }

    case 'SPLINE': {
      const approx = splineToPolyline(rec, scale);
      if (!approx) {
        warnings.push({
          code: 'unsupported_entity',
          message: '스플라인에 유효한 점이 2개 미만이라 생략했습니다.',
          subject: 'SPLINE',
        });
        return null;
      }
      warnings.push({
        code: 'curve_approximated',
        message: '스플라인을 다각형으로 근사했습니다 (매끄러운 곡선으로 다시 내보낼 수는 없습니다).',
        subject: 'SPLINE',
      });
      return { ...common(), type: 'polyline', points: approx.points, closed: approx.closed };
    }

    case 'POINT': {
      const center = readPoint(rec, 10, scale);
      // 점은 화면에 아무것도 안 그려지면 "사라진 것"과 구별이 안 된다.
      // 눈에 보이되 도면을 어지럽히지 않을 크기의 작은 원으로 표시한다.
      return { ...common(), type: 'circle', center, radius: mm(30) };
    }

    case 'SOLID':
    case '3DFACE': {
      // SOLID 는 꼭짓점이 지그재그 순서, 3DFACE 는 순서대로다.
      const quad = quadToPolyline(rec, scale, rec.type === 'SOLID');
      if (!quad) {
        warnings.push({
          code: 'unsupported_entity',
          message: `${rec.type} 의 꼭짓점이 부족해 생략했습니다.`,
          subject: rec.type,
        });
        return null;
      }
      // 채움은 표현하지 못한다 — 외곽선만 그린다. 형상은 남으므로 도면은 읽힌다.
      return { ...common(), type: 'polyline', points: quad.points, closed: quad.closed };
    }

    case 'VERTEX':
    case 'SEQEND':
    case 'ENDBLK':
    case 'BLOCK':
      return null;

    default: {
      const note = UNSUPPORTED_NOTE[rec.type];
      if (note) {
        warnings.push({
          code: 'unsupported_entity',
          message: `${note}는 아직 읽지 못해 생략했습니다.`,
          subject: rec.type,
        });
      }
      return null;
    }
  }
}

/** MTEXT 서식 코드 제거 — `\P` 는 줄바꿈, `{\fArial|b0;...}` 등은 버린다 */
function stripMTextFormatting(s: string): string {
  return s
    .replace(/\\P/g, ' ')
    .replace(/\\[A-Za-z][^;]*;/g, '')
    .replace(/[{}]/g, '')
    .trim();
}

/* -------------------------------------------------------------------------- *
 * 곡선 → 다각형 근사
 *
 * 우리 도형 모델은 직선·원·호까지만 있고 일반 곡선(베지어·NURBS)이 없다.
 * ELLIPSE 와 SPLINE 을 잘게 쪼갠 폴리선으로 바꿔 넣는다 — 완벽하진 않지만
 * "형체가 통째로 사라지는 것"보다 훨씬 낫고, 도면 검토·물량 산출에는
 * 이 정도 정밀도로 충분하다.
 * -------------------------------------------------------------------------- */

/** 타원(또는 타원호)을 잘게 나눈 폴리선으로 만든다. 데이터가 부족하면 null. */
function ellipseToPolyline(
  rec: Record0,
  scale: number
): { points: Pt[]; closed: boolean } | null {
  const cx = firstNum(rec, 10) ?? 0;
  const cy = firstNum(rec, 20) ?? 0;
  // 11/21 은 중심 기준 "장축 벡터" — DXF 스펙상 이미 상대값이다(중심을 빼면 안 된다).
  const mx = firstNum(rec, 11) ?? 0;
  const my = firstNum(rec, 21) ?? 0;
  const majorLen = Math.hypot(mx, my);
  if (majorLen === 0) return null;

  const ratio = firstNum(rec, 40) ?? 1;
  const minorLen = majorLen * ratio;
  const rot = Math.atan2(my, mx);

  const start = firstNum(rec, 41) ?? 0;
  const end = firstNum(rec, 42) ?? Math.PI * 2;
  const span = end - start;
  const full = Math.abs(Math.abs(span) - Math.PI * 2) < 1e-6;

  // 전체 원둘레 기준 64분할 — 화면에서 매끄러워 보이는 최소치.
  // 호(일부 구간)면 그 비율만큼만 나눈다.
  const segments = Math.max(8, Math.round(64 * Math.min(1, Math.abs(span) / (Math.PI * 2))));
  const points: Pt[] = [];
  const steps = full ? segments : segments; // 열린 호는 끝점까지 포함해야 하므로 아래서 <=

  for (let i = 0; i <= steps; i++) {
    if (full && i === steps) break; // 닫힌 도형은 시작점과 끝점이 같아 중복을 피한다
    const t = start + span * (i / steps);
    const lx = Math.cos(t) * majorLen;
    const ly = Math.sin(t) * minorLen;
    const wx = lx * Math.cos(rot) - ly * Math.sin(rot);
    const wy = lx * Math.sin(rot) + ly * Math.cos(rot);
    points.push({
      x: Math.round((cx + wx) * scale),
      y: Math.round((cy + wy) * scale),
    });
  }

  if (points.length < 2) return null;
  return { points, closed: full };
}

/** 스플라인을 다각형으로 근사한다. 곡선이 실제로 지나는 "적합점"이 있으면 그걸 쓰고,
 *  없으면 "조정점"(곡선을 당기는 뼈대)을 대신 쓴다 — 후자는 더 각지게 보일 수 있다. */
function splineToPolyline(rec: Record0, scale: number): { points: Pt[]; closed: boolean } | null {
  const fitXs = allNums(rec, 11);
  const fitYs = allNums(rec, 21);
  const useFit = Math.min(fitXs.length, fitYs.length) >= 2;

  const xs = useFit ? fitXs : allNums(rec, 10);
  const ys = useFit ? fitYs : allNums(rec, 20);
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return null;

  const points: Pt[] = [];
  for (let i = 0; i < n; i++) {
    points.push({ x: Math.round(xs[i]! * scale), y: Math.round(ys[i]! * scale) });
  }

  const closed = ((firstInt(rec, 70) ?? 0) & 1) !== 0;
  return { points, closed };
}

/* -------------------------------------------------------------------------- *
 * 그룹코드 조회 보조
 * -------------------------------------------------------------------------- */

function splitByCode0(pairs: DxfPair[]): Record0[] {
  const out: Record0[] = [];
  let cur: Record0 | null = null;
  for (const p of pairs) {
    if (p.code === 0) {
      if (cur) out.push(cur);
      cur = { type: p.value.trim().toUpperCase(), pairs: [] };
      continue;
    }
    if (cur) cur.pairs.push(p);
  }
  if (cur) out.push(cur);
  return out;
}

function firstString(rec: Record0, code: number): string | null {
  for (const p of rec.pairs) if (p.code === code) return p.value.trim();
  return null;
}

function firstNum(rec: Record0, code: number): number | null {
  for (const p of rec.pairs) if (p.code === code) return num(p.value);
  return null;
}

function firstInt(rec: Record0, code: number): number | null {
  for (const p of rec.pairs) if (p.code === code) return int(p.value);
  return null;
}

function allNums(rec: Record0, code: number): number[] {
  const out: number[] = [];
  for (const p of rec.pairs) if (p.code === code) out.push(num(p.value));
  return out;
}

/** 코드 n(=x), n+10(=y) 쌍을 점으로. 도면 단위 → µm. */
function readPoint(rec: Record0, code: number, scale: number): Pt {
  const x = firstNum(rec, code) ?? 0;
  const y = firstNum(rec, code + 10) ?? 0;
  return { x: Math.round(x * scale) as Micron, y: Math.round(y * scale) as Micron };
}

function colorOf(rec: Record0): string | null {
  const aci = firstInt(rec, 62);
  if (aci === null || aci === ACI_BY_LAYER) return null; // ByLayer
  return aciToHex(Math.abs(aci));
}
